import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useStateValue } from 'contexts'

// components
import { Modal } from 'components/common'

// types
import type { ReauthModalOptions } from 'contexts/reducers/ui'
import { AuthChallengeType } from 'types/graphql'

import { ReauthModalMFAStep, ReauthModalPasswordStep } from './components'

import scss from './index.module.scss'

const ReauthModal = ({
  onError,
  onComplete,
}: {
  onError: ReauthModalOptions['onError']
  onComplete: ReauthModalOptions['onComplete']
}): ReactElement => {
  const {
    actions: { uiActions },
  } = useStateValue()

  const [authChallengeType, setAuthChallengeType] =
    useState<AuthChallengeType | null>(null)

  const onClose = useCallback(() => {
    uiActions.setReauthModalOptions(null)
  }, [uiActions])

  const modalContent = useMemo(() => {
    switch (authChallengeType) {
      // For now these are the only MFA types supported by our frontend and backend
      case AuthChallengeType.SmsMfa:
      case AuthChallengeType.SoftwareTokenMfa:
        return (
          <ReauthModalMFAStep
            authChallengeType={authChallengeType}
            onComplete={onComplete}
            onClose={onClose}
          />
        )
      default:
        return (
          <ReauthModalPasswordStep
            onComplete={onComplete}
            onClose={onClose}
            setAuthChallengeType={setAuthChallengeType}
          />
        )
    }
  }, [authChallengeType, onComplete, onClose])

  return (
    <Modal
      isShowing
      title='Re-authentication required'
      size='small'
      hide={() => {
        onError('Re-auth cancelled')
        onClose()
      }}
      overlayClassName={scss.modalOverlay}
      wrapperClassName={scss.modalWrapper}
    >
      <div className={scss.modalContent}>{modalContent}</div>
    </Modal>
  )
}

export default ReauthModal
